import $ from 'jquery';
import ViewportTracker from '../../lib/ViewportTracker';
 
export interface MainMenuConfig {
	menu: string;
	menuTriggers: string;
	nodeTrigger: string;
	styleClass: {
		menuOpen: string;
		nodeOpen: string;
	};
	listeners?: Array<MainMenuListener>;
	menuTriggerEvent?: string;	// Default is 'click'
}

export interface MainMenuListener {
	process(event: MainMenuEvent): void;
}

export interface MainMenuEvent {
}
export class ToggleNodeEvent implements MainMenuEvent {
	/**
	 * @param listItem
	 * @param open
	 * @param uiEvent undefined unless user interaction initiated the toggle
	 */
	constructor(public listItem: JQuery,
				public open: boolean,
				public uiEvent?: UIEvent) {
	}
}

/**
 * Uses jQuery slideUp/Down to open close children if configured to do so
 */
export class CollapseExpandChildrenListener implements MainMenuListener {
	constructor(private readonly duration: number,
				private readonly easing: string) {}

	process(event: MainMenuEvent): void {
		if (event instanceof ToggleNodeEvent) {
			if (ViewportTracker.below('mobileMenu', true)) {
				// Displaying mobile menu

				if (event.uiEvent !== undefined) {
					// Do not want click to follow link. Instead, we are going
					// to expand/collapse list of child pages.
					event.uiEvent.preventDefault();
				}

				const elementSlide = event.listItem.children('div');

				elementSlide.stop();
				if (event.open) {
					elementSlide.slideDown(this.duration, this.easing);
				} else {
					elementSlide.slideUp(this.duration, this.easing);
				}
			}
		}
	}
}

export class MainMenu {
	private readonly menu: JQuery;
	private readonly listeners: MainMenuListener[];

	constructor(private config: MainMenuConfig) {
		this.menu = $(config.menu);
		this.listeners = config.listeners === undefined ? [] : [...config.listeners];

		// Open/close menu
		$(config.menuTriggers).on(config.menuTriggerEvent ?? 'click', (event) => {
			event.preventDefault();

			this.toggleMenu();
		});

		// Open/close node
		this.menu.on('click', this.config.nodeTrigger, event => {
			const target = event.originalEvent?.target;
			if (!(target instanceof HTMLElement)) {
				return;
			}
			const anchor = target instanceof HTMLAnchorElement ? $(target) : $(target).parent('a');
			const listItem = anchor.parent('li');
			this.toggleNode(listItem, !this.isNodeOpen(listItem), event.originalEvent);
		});

		// Open active
		this.openActive();
	}

	public addListener(listener: MainMenuListener) {
		this.listeners.push(listener);
	}

	public openActive(): void {
		const nodeTriggers = this.menu.find(this.config.nodeTrigger);
		this.toggleNode(nodeTriggers.not('.active').parent(), false);
		this.toggleNode(nodeTriggers.filter('.active').parent(), true);
	}

	public toggleMenu(open?: boolean): void {
		document.body.classList.toggle(this.config.styleClass.menuOpen, open);
	}

	private isNodeOpen(listItem: JQuery): boolean {
		return listItem.hasClass(this.config.styleClass.nodeOpen);
	}

	/**
	 * @param listItem
	 * @param open
	 * @param uiEvent undefined unless user interaction initiated the toggle
	 */
	private toggleNode(listItem: JQuery, open: boolean, uiEvent?: UIEvent): void {
		this.broadcast(new ToggleNodeEvent(listItem, open, uiEvent));

		// Add/remove nodeOpen class
		listItem.toggleClass(this.config.styleClass.nodeOpen, open);
	}
	private broadcast(event: MainMenuEvent): void {
		for (const listener of this.listeners) {
			listener.process(event);
		}
	}

	public closeAllNodes(): void {
		const activeTriggers = this.menu.find(this.config.nodeTrigger).parent().filter('.' + this.config.styleClass.nodeOpen);
		this.toggleNode(activeTriggers, false);
	}
}
